/* ============================================================================
   store-locations-swiper.js
   ----------------------------------------------------------------------------
   Extends the shared <swiper-component> (assets/swiper-component.js) with a
   custom <select> dropdown that is kept in two-way sync with the active slide.

   Markup contract (see sections/store-locations.liquid):
     <div data-store-locations-root>
       <select data-store-locations-select>...</select>
       <store-locations-swiper class="swiper" data-*="...">
         <div class="swiper-wrapper">...</div>
       </store-locations-swiper>
     </div>

   The parent class handles config parsing, breakpoints, arrows, dots,
   autoplay, loop, theme-editor refresh, and cleanup. We only layer on the
   dropdown wiring.
   ============================================================================ */

(function defineStoreLocationsSwiper() {
  if (customElements.get('store-locations-swiper')) return;

  const register = () => {
    if (!window.SwiperComponent) return false;

    class StoreLocationsSwiper extends window.SwiperComponent {
      constructor() {
        super();
        this.selectEl = null;
        this._onSelectChange = this._onSelectChange.bind(this);
        this._onSlideChange = this._onSlideChange.bind(this);
      }

      initSwiper() {
        super.initSwiper();
        if (!this.swiper) return;

        const root = this.closest('[data-store-locations-root]') || this.parentElement;
        this.selectEl = root ? root.querySelector('[data-store-locations-select]') : null;
        if (!this.selectEl) return;

        this.selectEl.addEventListener('change', this._onSelectChange);
        this.swiper.on('slideChange', this._onSlideChange);
        this._syncSelectToSlide();
      }

      _currentIndex() {
        if (!this.swiper) return 0;
        return typeof this.swiper.realIndex === 'number'
          ? this.swiper.realIndex
          : this.swiper.activeIndex;
      }

      _syncSelectToSlide() {
        if (!this.selectEl || !this.swiper) return;
        this.selectEl.value = String(this._currentIndex());
      }

      _onSelectChange(event) {
        const idx = parseInt(event.target.value, 10);
        if (!this.swiper || Number.isNaN(idx)) return;
        if (typeof this.swiper.slideToLoop === 'function' && this.swiper.params.loop) {
          this.swiper.slideToLoop(idx);
        } else {
          this.swiper.slideTo(idx);
        }
      }

      _onSlideChange() {
        this._syncSelectToSlide();
      }

      disconnectedCallback() {
        if (this.selectEl) {
          this.selectEl.removeEventListener('change', this._onSelectChange);
          this.selectEl = null;
        }
        if (super.disconnectedCallback) super.disconnectedCallback();
      }
    }

    customElements.define('store-locations-swiper', StoreLocationsSwiper);
    return true;
  };

  if (!register()) {
    document.addEventListener('DOMContentLoaded', register, { once: true });
  }
})();
